export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  strength: 'weak' | 'medium' | 'strong';
  checks: {
    minLength: boolean;
    hasUppercase: boolean;
    hasNumber: boolean;
    hasSpecialChar: boolean;
    noName: boolean;
    noContinuous: boolean;
  };
}

export const validatePassword = (
  password: string,
  firstName?: string,
  lastName?: string
): PasswordValidationResult => {
  const errors: string[] = [];
  const checks = {
    minLength: false,
    hasUppercase: false,
    hasNumber: false,
    hasSpecialChar: false,
    noName: true,
    noContinuous: true,
  };

  // Check minimum length (8 characters)
  if (password.length >= 8) {
    checks.minLength = true;
  } else {
    errors.push('Password must be at least 8 characters long');
  }

  // Check for uppercase letter
  if (/[A-Z]/.test(password)) {
    checks.hasUppercase = true;
  } else {
    errors.push('Password must contain at least one uppercase letter');
  }

  // Check for number
  if (/\d/.test(password)) {
    checks.hasNumber = true;
  } else {
    errors.push('Password must contain at least one number');
  }

  // Check for special character
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/.test(password)) {
    checks.hasSpecialChar = true;
  } else {
    errors.push('Password must contain at least one special character');
  }

  // Check if password contains user's name
  if (firstName || lastName) {
    const passwordLower = password.toLowerCase();
    const firstNameLower = firstName?.toLowerCase() || '';
    const lastNameLower = lastName?.toLowerCase() || '';
    
    if (
      (firstNameLower.length >= 3 && passwordLower.includes(firstNameLower)) ||
      (lastNameLower.length >= 3 && passwordLower.includes(lastNameLower))
    ) {
      checks.noName = false;
      errors.push('Password should not contain your name');
    }
  }

  // Check for continuous numbers or alphabets (3 or more consecutive)
  const hasContinuousNumbers = /(\d)\1{2,}/.test(password) || // Same number repeated 3+ times
    /012|123|234|345|456|567|678|789|890|987|876|765|654|543|432|321|210/.test(password);
  
  const hasContinuousAlphabets = /abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|zyx|yxw|xwv|wvu|vut|uts|tsr|srq|rqp|qpo|pon|onm|nml|mlk|lkj|kji|jih|ihg|hgf|gfe|fed|edc|dcb|cba/i.test(password);

  if (hasContinuousNumbers || hasContinuousAlphabets) {
    checks.noContinuous = false;
    errors.push('Password should not contain continuous numbers or alphabets');
  }

  // Calculate strength
  const validChecks = Object.values(checks).filter(Boolean).length;
  let strength: 'weak' | 'medium' | 'strong' = 'weak';
  
  if (validChecks >= 6) {
    strength = 'strong';
  } else if (validChecks >= 4) {
    strength = 'medium';
  }

  return {
    isValid: errors.length === 0,
    errors,
    strength,
    checks,
  };
};

export const getPasswordStrengthColor = (strength: 'weak' | 'medium' | 'strong'): string => {
  switch (strength) {
    case 'weak':
      return 'text-red-600';
    case 'medium':
      return 'text-yellow-600';
    case 'strong':
      return 'text-green-600';
    default:
      return 'text-gray-600';
  }
};

export const getPasswordStrengthBg = (strength: 'weak' | 'medium' | 'strong'): string => {
  switch (strength) {
    case 'weak':
      return 'bg-red-200';
    case 'medium':
      return 'bg-yellow-200';
    case 'strong':
      return 'bg-green-200';
    default:
      return 'bg-gray-200';
  }
};